/**
 * Palette Export — consolidated from SPA and shared-logic implementations.
 *
 * All text-based formats from the SPA are included. Binary formats that
 * require browser APIs (PNG, PDF) are excluded and remain in the SPA.
 *
 * Formats: css, scss, json, js, swift, swiftui, android, flutter, kotlin,
 *          tailwind, gpl, svg, swatches, gradient-css, figma-tokens,
 *          material-tokens, rest-payload, ase, act, clr
 */
import Color from 'colorjs.io';
// ── Helpers ─────────────────────────────────────────────────────────────────
function hexToRGB(hex) {
    const clean = hex.replace('#', '');
    return {
        r: parseInt(clean.substring(0, 2), 16),
        g: parseInt(clean.substring(2, 4), 16),
        b: parseInt(clean.substring(4, 6), 16),
    };
}
function hexToAndroidArgb(hex) {
    const clean = hex.replace('#', '');
    return `#FF${clean.toUpperCase()}`;
}
function hexToARGB(hex) {
    const clean = hex.replace('#', '').toUpperCase();
    return `0xFF${clean}`;
}
function toPascalCase(str) {
    return str
        .replace(/[^a-zA-Z0-9]+/g, ' ')
        .split(/\s+/)
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join('');
}
function slugify(str) {
    return str
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
}
function rgbToHsl(r, g, b) {
    const rn = r / 255;
    const gn = g / 255;
    const bn = b / 255;
    const max = Math.max(rn, gn, bn);
    const min = Math.min(rn, gn, bn);
    const l = (max + min) / 2;
    if (max === min)
        return { h: 0, s: 0, l: Math.round(l * 100) };
    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h = 0;
    if (max === rn)
        h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
    else if (max === gn)
        h = ((bn - rn) / d + 2) / 6;
    else
        h = ((rn - gn) / d + 4) / 6;
    return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}
async function generateFlutterShadeMap(hex) {
    const base = new Color(hex).to('oklch');
    const [baseL, baseC, baseH] = base.coords;
    const l = baseL ?? 0.5;
    const c = baseC ?? 0;
    const h = isNaN(baseH) ? 0 : baseH;
    const shadeLightness = {
        50: Math.min(0.97, l + 0.40),
        100: Math.min(0.95, l + 0.32),
        200: Math.min(0.90, l + 0.22),
        300: Math.min(0.85, l + 0.13),
        400: Math.min(0.80, l + 0.06),
        500: l,
        600: Math.max(0.15, l - 0.08),
        700: Math.max(0.12, l - 0.16),
        800: Math.max(0.10, l - 0.24),
        900: Math.max(0.08, l - 0.32),
    };
    const shades = {};
    for (const [shade, targetL] of Object.entries(shadeLightness)) {
        const shadeColor = new Color('oklch', [targetL, c, h]);
        const srgb = shadeColor.to('srgb').toGamut({ space: 'srgb' });
        shades[Number(shade)] = srgb.toString({ format: 'hex' }).replace('#', '');
    }
    return shades;
}
/**
 * Get the list of available text-based export formats.
 */
export function getAvailableExportFormats() {
    return [
        { value: 'css', label: 'CSS Custom Properties' },
        { value: 'scss', label: 'SCSS Variables' },
        { value: 'json', label: 'JSON' },
        { value: 'js', label: 'JavaScript Module' },
        { value: 'swift', label: 'Swift UIColor' },
        { value: 'swiftui', label: 'SwiftUI' },
        { value: 'android', label: 'Android XML' },
        { value: 'flutter', label: 'Flutter MaterialColor' },
        { value: 'kotlin', label: 'Jetpack Compose' },
        { value: 'tailwind', label: 'Tailwind Config' },
        { value: 'gpl', label: 'GIMP Palette' },
        { value: 'svg', label: 'SVG Swatches' },
        { value: 'swatches', label: 'Swatches JSON' },
        { value: 'gradient-css', label: 'Gradient CSS' },
        { value: 'figma-tokens', label: 'Figma Tokens' },
        { value: 'material-tokens', label: 'Material Tokens' },
        { value: 'rest-payload', label: 'REST Payload' },
        { value: 'ase', label: 'Adobe Swatch Exchange' },
        { value: 'act', label: 'Adobe Color Table' },
        { value: 'clr', label: 'macOS Color Picker' },
    ];
}
/**
 * Export a color palette in the specified format.
 */
export async function exportPalette(colors, names, format, options) {
    const paletteName = options?.paletteName ?? 'Palette';
    let content = '';
    let binary;
    let filename = `palette.${format}`;
    switch (format) {
        // ── CSS ────────────────────────────────────────────────────────────────
        case 'css':
            content =
                ':root {\n' +
                    colors.map((hex, i) => `  --${names[i]}: ${hex};`).join('\n') +
                    '\n}';
            break;
        case 'scss':
            content = colors.map((hex, i) => `$${names[i]}: ${hex};`).join('\n');
            break;
        // ── Data ───────────────────────────────────────────────────────────────
        case 'json':
            content = JSON.stringify(Object.fromEntries(colors.map((hex, i) => [names[i], hex])), null, 2);
            break;
        case 'js':
            content =
                `export const palette = {\n` +
                    colors.map((hex, i) => `  ${names[i]}: '${hex}',`).join('\n') +
                    `\n};`;
            filename = 'palette.js';
            break;
        // ── Swift ──────────────────────────────────────────────────────────────
        case 'swift': {
            const swiftColors = colors.map((hex, i) => {
                const name = names[i].replace(/-/g, '_');
                return `    static let ${name} = UIColor(hex: "${hex}")`;
            }).join('\n');
            content = `import UIKit

extension UIColor {
    convenience init(hex: String) {
        let hex = hex.trimmingCharacters(in: CharacterSet.alphanumerics.inverted)
        var int: UInt64 = 0
        Scanner(string: hex).scanHexInt64(&int)
        let a, r, g, b: UInt64
        switch hex.count {
        case 6:
            (a, r, g, b) = (255, int >> 16, int >> 8 & 0xFF, int & 0xFF)
        case 8:
            (a, r, g, b) = (int >> 24, int >> 16 & 0xFF, int >> 8 & 0xFF, int & 0xFF)
        default:
            (a, r, g, b) = (255, 0, 0, 0)
        }
        self.init(
            red: CGFloat(r) / 255,
            green: CGFloat(g) / 255,
            blue: CGFloat(b) / 255,
            alpha: CGFloat(a) / 255
        )
    }
}

extension UIColor {
${swiftColors}
}`;
            filename = 'Colors.swift';
            break;
        }
        case 'swiftui': {
            const structName = `${toPascalCase(paletteName)}Palette`;
            let swift = `import SwiftUI\n\nstruct ${structName} {\n`;
            colors.forEach((hex, i) => {
                const rgb = hexToRGB(hex);
                swift += `    static let color${i + 1} = Color(red: ${(rgb.r / 255).toFixed(3)}, green: ${(rgb.g / 255).toFixed(3)}, blue: ${(rgb.b / 255).toFixed(3)}, opacity: 1.0)\n`;
            });
            swift += `}\n`;
            content = swift;
            filename = 'Colors.swift';
            break;
        }
        // ── Android ────────────────────────────────────────────────────────────
        case 'android': {
            const androidColors = colors.map((hex, i) => {
                const name = names[i].replace(/-/g, '_');
                return `    <color name="${name}">${hexToAndroidArgb(hex)}</color>`;
            }).join('\n');
            content = `<?xml version="1.0" encoding="utf-8"?>\n<resources>\n${androidColors}\n</resources>`;
            filename = 'colors.xml';
            break;
        }
        // ── Flutter ────────────────────────────────────────────────────────────
        case 'flutter': {
            const flutterEntries = [];
            for (let i = 0; i < colors.length; i++) {
                const hex = colors[i];
                const name = names[i].replace(/-/g, '_');
                const cleanHex = hex.replace('#', '');
                const shades = await generateFlutterShadeMap(hex);
                flutterEntries.push(`  static const MaterialColor ${name} = MaterialColor(\n` +
                    `    0xFF${cleanHex},\n` +
                    `    <int, Color>{\n` +
                    Object.entries(shades)
                        .sort(([a], [b]) => Number(a) - Number(b))
                        .map(([shade, h]) => `      ${shade}: Color(0xFF${h}),`)
                        .join('\n') +
                    `\n    },\n  );`);
            }
            content = `import 'package:flutter/material.dart';\n\nclass AppColors {\n${flutterEntries.join('\n\n')}\n}`;
            filename = 'app_colors.dart';
            break;
        }
        // ── Kotlin (Jetpack Compose) ───────────────────────────────────────────
        case 'kotlin': {
            const objectName = `${toPascalCase(paletteName)}Palette`;
            let kotlin = `import androidx.compose.ui.graphics.Color\n\nobject ${objectName} {\n`;
            colors.forEach((hex, i) => {
                kotlin += `    val Color${i + 1} = Color(${hexToARGB(hex)})\n`;
            });
            kotlin += `}\n`;
            content = kotlin;
            filename = 'Palette.kt';
            break;
        }
        // ── Tailwind ───────────────────────────────────────────────────────────
        case 'tailwind': {
            const twColors = {};
            colors.forEach((hex, i) => { twColors[names[i]] = hex; });
            content = `module.exports = ${JSON.stringify({ theme: { extend: { colors: twColors } } }, null, 2)}`;
            filename = 'tailwind.config.js';
            break;
        }
        // ── GIMP Palette ───────────────────────────────────────────────────────
        case 'gpl': {
            let gpl = `GIMP Palette\nName: ${paletteName}\nColumns: 0\n#\n`;
            colors.forEach((hex, i) => {
                const rgb = hexToRGB(hex);
                gpl += `${rgb.r.toString().padStart(3)} ${rgb.g.toString().padStart(3)} ${rgb.b.toString().padStart(3)} ${names[i]}\n`;
            });
            content = gpl;
            filename = `${slugify(paletteName)}.gpl`;
            break;
        }
        // ── SVG ────────────────────────────────────────────────────────────────
        case 'svg': {
            const swatchSize = 100;
            const width = colors.length * swatchSize;
            let svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${swatchSize}" viewBox="0 0 ${width} ${swatchSize}">\n`;
            svg += `  <title>${paletteName}</title>\n`;
            colors.forEach((hex, i) => {
                svg += `  <rect x="${i * swatchSize}" y="0" width="${swatchSize}" height="${swatchSize}" fill="${hex}" />\n`;
            });
            svg += `</svg>`;
            content = svg;
            filename = `${slugify(paletteName)}.svg`;
            break;
        }
        // ── Swatches JSON ──────────────────────────────────────────────────────
        case 'swatches': {
            const swatches = {
                name: paletteName,
                colors: colors.map((hex, i) => {
                    const rgb = hexToRGB(hex);
                    return { name: names[i], red: rgb.r / 255, green: rgb.g / 255, blue: rgb.b / 255, alpha: 1.0 };
                }),
            };
            content = JSON.stringify(swatches, null, 2);
            filename = `${slugify(paletteName)}.swatches`;
            break;
        }
        // ── Gradient CSS ───────────────────────────────────────────────────────
        case 'gradient-css': {
            let css = `/* ${paletteName} - Gradient CSS */\n\n`;
            css += `.gradient-linear {\n  background: linear-gradient(to right, ${colors.join(', ')});\n}\n\n`;
            css += `.gradient-radial {\n  background: radial-gradient(circle, ${colors.join(', ')});\n}\n\n`;
            css += `.gradient-conic {\n  background: conic-gradient(${colors.join(', ')});\n}\n`;
            if (colors.length >= 2) {
                const stops = colors.map((hex, i) => `${hex} ${((i / (colors.length - 1)) * 100).toFixed(1)}%`);
                css += `\n.gradient-custom {\n  background: linear-gradient(135deg, ${stops.join(', ')});\n}\n`;
            }
            content = css;
            filename = 'gradients.css';
            break;
        }
        // ── Figma Tokens ───────────────────────────────────────────────────────
        case 'figma-tokens': {
            const tokens = {};
            colors.forEach((hex, i) => {
                tokens[names[i]] = { value: hex, type: 'color', description: `Color ${i + 1} from ${paletteName}` };
            });
            content = JSON.stringify({ [paletteName]: tokens }, null, 2);
            filename = 'figma-tokens.json';
            break;
        }
        // ── Material Tokens ────────────────────────────────────────────────────
        case 'material-tokens': {
            const slug = slugify(paletteName);
            const materialTokens = {};
            colors.forEach((hex, i) => {
                materialTokens[`md.sys.color.${slug}.${i + 1}`] = {
                    $type: 'color', $value: hex, $description: `Color ${i + 1} from ${paletteName}`,
                };
            });
            content = JSON.stringify({
                $schema: 'https://json.schemastore.org/design-tokens.json',
                $metadata: { id: slug, label: paletteName },
                color: { palette: { [slug]: materialTokens } },
            }, null, 2);
            filename = 'material-tokens.json';
            break;
        }
        // ── REST Payload ───────────────────────────────────────────────────────
        case 'rest-payload': {
            const slug = slugify(paletteName);
            content = JSON.stringify({
                palette: { id: slug, name: paletteName, colorCount: colors.length },
                colors: colors.map((hex, i) => ({
                    id: names[i], hex, rgb: hexToRGB(hex), hsl: rgbToHsl(...Object.values(hexToRGB(hex))), order: i,
                })),
            }, null, 2);
            filename = 'palette-payload.json';
            break;
        }
        // ── ASE (Adobe Swatch Exchange) — binary as Uint8Array ─────────────────
        case 'ase': {
            const blocks = [];
            // Signature: ASEF
            blocks.push(0x41, 0x53, 0x45, 0x46);
            // Version: 1.0
            blocks.push(0x00, 0x01, 0x00, 0x00);
            // Number of blocks (big-endian 32-bit)
            const n = colors.length;
            blocks.push((n >> 24) & 0xff, (n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff);
            for (let i = 0; i < colors.length; i++) {
                const rgb = hexToRGB(colors[i]);
                // Block type: color entry (0x0001)
                blocks.push(0x00, 0x01);
                // Build block body
                const name = names[i] || `Color ${i + 1}`;
                const nameChars = [];
                for (let j = 0; j < name.length; j++) {
                    nameChars.push(0x00, name.charCodeAt(j));
                }
                nameChars.push(0x00, 0x00); // null terminator
                // Block length = 2 (name len) + name bytes + 4 (color model) + 12 (3 floats) + 2 (color mode)
                const nameLen = name.length + 1;
                const blockLen = 2 + nameChars.length + 4 + 12 + 2;
                blocks.push((blockLen >> 24) & 0xff, (blockLen >> 16) & 0xff, (blockLen >> 8) & 0xff, blockLen & 0xff);
                // Name length (big-endian 16-bit)
                blocks.push((nameLen >> 8) & 0xff, nameLen & 0xff);
                blocks.push(...nameChars);
                // Color model: "RGB "
                blocks.push(0x52, 0x47, 0x42, 0x20);
                // RGB values as 32-bit floats
                const floatView = new DataView(new ArrayBuffer(12));
                floatView.setFloat32(0, rgb.r / 255, false);
                floatView.setFloat32(4, rgb.g / 255, false);
                floatView.setFloat32(8, rgb.b / 255, false);
                for (let j = 0; j < 12; j++)
                    blocks.push(floatView.getUint8(j));
                // Color mode: global (0x0000)
                blocks.push(0x00, 0x00);
            }
            binary = new Uint8Array(blocks);
            content = `[Binary ASE file — ${binary.length} bytes]`;
            filename = `${slugify(paletteName)}.ase`;
            break;
        }
        // ── ACT (Adobe Color Table) — binary as Uint8Array ────────────────────
        case 'act': {
            const data = new Uint8Array(768);
            colors.forEach((hex, i) => {
                if (i >= 256)
                    return;
                const rgb = hexToRGB(hex);
                data[i * 3] = rgb.r;
                data[i * 3 + 1] = rgb.g;
                data[i * 3 + 2] = rgb.b;
            });
            binary = data;
            content = `[Binary ACT file — 768 bytes]`;
            filename = `${slugify(paletteName)}.act`;
            break;
        }
        // ── CLR (macOS Color Picker — XML plist) ──────────────────────────────
        case 'clr': {
            let plist = `<?xml version="1.0" encoding="UTF-8"?>\n`;
            plist += `<!DOCTYPE plist PUBLIC "-//Apple//DTD PLIST 1.0//EN" "http://www.apple.com/DTDs/PropertyList-1.0.dtd">\n`;
            plist += `<plist version="1.0">\n<dict>\n`;
            plist += `  <key>$archiver</key>\n  <string>NSKeyedArchiver</string>\n`;
            plist += `  <key>$objects</key>\n  <array>\n    <string>$null</string>\n`;
            colors.forEach((hex, i) => {
                const rgb = hexToRGB(hex);
                plist += `    <dict>\n`;
                plist += `      <key>name</key>\n      <string>${names[i]}</string>\n`;
                plist += `      <key>red</key>\n      <real>${(rgb.r / 255).toFixed(6)}</real>\n`;
                plist += `      <key>green</key>\n      <real>${(rgb.g / 255).toFixed(6)}</real>\n`;
                plist += `      <key>blue</key>\n      <real>${(rgb.b / 255).toFixed(6)}</real>\n`;
                plist += `      <key>alpha</key>\n      <real>1.0</real>\n`;
                plist += `    </dict>\n`;
            });
            plist += `  </array>\n</dict>\n</plist>\n`;
            content = plist;
            filename = `${slugify(paletteName)}.clr`;
            break;
        }
        default:
            content = JSON.stringify({ colors, format });
    }
    const size = binary ? binary.length : Buffer.byteLength(content, 'utf-8');
    return { content, binary, format, filename, size };
}
//# sourceMappingURL=paletteExport.js.map